import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, Users, Globe, Smartphone, Monitor, MapPin, Calendar, Eye, QrCode, Nfc, Link2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { nfcCode } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [nfcCode]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/${nfcCode}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-muted-foreground">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">No analytics data available</p>
        </div>
      </div>
    );
  }

  const stats = analytics.stats;
  const locations = analytics.locations;
  const scanSources = analytics.scan_sources || { nfc: 0, qr: 0, link: 0 };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div className="mb-12">
            <button
              data-testid="back-button"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span>Back to dashboard</span>
            </button>
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-8 h-8 text-primary" strokeWidth={1.5} />
              <h1 className="text-4xl md:text-5xl font-bold font-heading tracking-tight">
                Analytics
              </h1>
            </div>
            <p className="text-lg text-muted-foreground">
              Tag: <code className="font-mono bg-secondary px-3 py-1 rounded-lg">{nfcCode}</code>
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <Eye className="w-8 h-8 text-primary mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">Total Views</div>
              <div className="text-4xl font-bold font-heading">{stats.total_views}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <Users className="w-8 h-8 text-primary mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">Unique Visitors</div>
              <div className="text-4xl font-bold font-heading">{stats.unique_visitors}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <Smartphone className="w-8 h-8 text-primary mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">Mobile</div>
              <div className="text-4xl font-bold font-heading">{stats.mobile_views}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <Monitor className="w-8 h-8 text-primary mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">Desktop</div>
              <div className="text-4xl font-bold font-heading">{stats.desktop_views}</div>
            </motion.div>
          </div>

          {/* Scan Sources */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <Nfc className="w-8 h-8 text-blue-500 mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">NFC Taps</div>
              <div className="text-4xl font-bold font-heading text-blue-600">{scanSources.nfc}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <QrCode className="w-8 h-8 text-green-500 mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">QR Scans</div>
              <div className="text-4xl font-bold font-heading text-green-600">{scanSources.qr}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
              className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8"
            >
              <Link2 className="w-8 h-8 text-purple-500 mb-4" strokeWidth={1.5} />
              <div className="text-sm font-medium text-muted-foreground mb-2">Shared Links</div>
              <div className="text-4xl font-bold font-heading text-purple-600">{scanSources.link}</div>
            </motion.div>
          </div>

          {/* Location Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Countries */}
            <div className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-6 h-6 text-primary" strokeWidth={1.5} />
                <h2 className="text-2xl font-bold font-heading">Top Countries</h2>
              </div>
              {Object.keys(locations.countries).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(locations.countries).map(([country, count], index) => (
                    <div key={country} className="flex items-center justify-between p-4 bg-secondary/30 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-muted-foreground">#{index + 1}</span>
                        <span className="font-medium">{country}</span>
                      </div>
                      <span className="text-lg font-bold text-primary">{count} views</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No location data yet</p>
              )}
            </div>

            {/* Cities */}
            <div className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-primary" strokeWidth={1.5} />
                <h2 className="text-2xl font-bold font-heading">Top Cities</h2>
              </div>
              {Object.keys(locations.cities).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(locations.cities).slice(0, 5).map(([city, count], index) => (
                    <div key={city} className="flex items-center justify-between p-4 bg-secondary/30 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-muted-foreground">#{index + 1}</span>
                        <span className="font-medium">{city}</span>
                      </div>
                      <span className="text-lg font-bold text-primary">{count} views</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No location data yet</p>
              )}
            </div>
          </div>

          {/* Recent Views */}
          <div className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <h2 className="text-2xl font-bold font-heading">Recent Views</h2>
            </div>
            {analytics.recent_views && analytics.recent_views.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">Time</th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">Source</th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">Location</th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">Device</th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">Browser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.recent_views.map((view, index) => (
                      <tr key={index} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                        <td className="py-3 px-4 text-sm">
                          {new Date(view.viewed_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            view.scan_source === 'qr' 
                              ? 'bg-green-100 text-green-700' 
                              : view.scan_source === 'link'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {view.scan_source === 'qr' ? (
                              <><QrCode className="w-3 h-3" /> QR</>
                            ) : view.scan_source === 'link' ? (
                              <><Link2 className="w-3 h-3" /> Link</>
                            ) : (
                              <><Nfc className="w-3 h-3" /> NFC</>
                            )}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {view.city}, {view.country}
                        </td>
                        <td className="py-3 px-4 text-sm capitalize">
                          {view.device_type}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {view.browser}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground">No views yet</p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
